import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, SafeAreaView, ScrollView, TouchableOpacity, Image, TextInput, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Colors, SIZES } from '../../constants/theme';
import { useFoodStore } from '../../store/useFoodStore';
import { firestoreService } from '../../services/firestoreService';
import RotatingPromoBanner, { PromoBannerItem } from '../../components/RotatingPromoBanner';
import { useUserLocation } from '../../hooks/useUserLocation';
import { offlineCache } from '../../services/offlineCache';
import { haversineKm, zonaDe } from '../../services/geoUtils';
import { ratingDisplay } from '../../services/ratingUtils';
import { useAppStore } from '../../store/useAppStore';
import { pesos } from '../../utils/dinero';
import { CATEGORIAS_DE_NEGOCIO, idDeCategoria, nombreDeCategoria } from '../../constants/categoriasDeNegocio';

// ⚠️ LA LISTA DE CATEGORIAS YA NO VIVE AQUI.
// Habia una copia en este archivo y otra en `restaurant-registration.tsx`, y se
// desincronizaron: el restaurante guardaba «Comida Dominicana» y esta pantalla
// buscaba «Dominicana». Como el filtro del servidor es `==`, esos restaurantes
// NO SALIAN. Ahora hay una sola lista, en constants/categoriasDeNegocio.ts.

export default function FoodScreen() {
  const router = useRouter();
  const restaurants = useFoodStore((state) => state.restaurants);
  const cart = useFoodStore((state) => state.cart);
  const setRestaurants = useFoodStore((state) => state.setRestaurants);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const direccionEntrega = useAppStore((s) => s.selectedDeliveryAddress);

  const [isOffline, setIsOffline] = useState(false);

  // 1) Cache-first: pinta lo último guardado antes de abrir red
  useEffect(() => {
    let cancelled = false;
    offlineCache.get<any[]>('restaurants').then((cached) => {
      if (cancelled) return;
      if (cached && cached.length > 0 && restaurants.length === 0) {
        setRestaurants(cached);
      }
    });
    return () => { cancelled = true; };
  }, [setRestaurants, restaurants.length]);

  // H72 (auditoría 16-sep-2026): esta pantalla no tenía NINGÚN estado de
  // carga. Con la lista aún vacía decía "No encontramos restaurantes para esta
  // búsqueda" desde el primer instante, sin haber buscado nada: con la red
  // lenta de un móvil eso duraba segundos, y quien abría Comida (el revisor de
  // Google entra por aquí en modo invitado) concluía que la app no tiene
  // restaurantes y se iba. Mismo patrón que (tabs)/activity.tsx: no se afirma
  // "no hay" hasta el PRIMER aviso del listener, que llega también cuando la
  // consulta falla (el servicio avisa con `[]`).
  //
  // Lleva reloj igual: si en 20 s no llegó nada, se deja de prometer que
  // "está cargando" — un "Cargando…" eterno es otro cuelgue. Si los datos
  // llegan después, la lista aparece sola.
  const [restaurantesRecibidos, setRestaurantesRecibidos] = useState(false);
  useEffect(() => {
    if (restaurantesRecibidos) return;
    const reloj = setTimeout(() => setRestaurantesRecibidos(true), 20000);
    return () => clearTimeout(reloj);
  }, [restaurantesRecibidos]);

  // 2) Listener paginado (server-side filter por categoría + límite 30)
  useEffect(() => {
    setRestaurantesRecibidos(false);
    const unsubscribe = firestoreService.listenToRestaurantsPaginated(
      { pageSize: 30, category: activeCategory },
      (liveRestaurants: any[]) => {
        setRestaurantesRecibidos(true);
        setRestaurants(liveRestaurants);
        setIsOffline(false);
        // Persiste para próxima sesión sin internet
        offlineCache.set('restaurants', liveRestaurants).catch(() => {});
      }
    );
    return () => unsubscribe();
  }, [setRestaurants, activeCategory]);

  // 3) Ubicación del usuario (puntual, no tracking)
  const userLoc = useUserLocation();

  // Guardamos SOLO la celda (~22 km), no la coordenada exacta: alcanza para
  // avisarle de ofertas cercanas y no expone dónde vive. Se escribe únicamente
  // cuando cambia de zona, para no gastar datos ni escrituras.
  const zonaGuardadaRef = React.useRef<string | null>(null);
  useEffect(() => {
    const uid = useAppStore.getState().userFirebaseUid;
    if (!uid || !userLoc) return;
    const zona = zonaDe(userLoc.lat, userLoc.lng);
    if (zonaGuardadaRef.current === zona) return;
    zonaGuardadaRef.current = zona;
    firestoreService
      .updateUserProfile(uid, { zone: zona })
      .catch((e) => console.warn('[food] no se pudo guardar la zona:', e?.message));
  }, [userLoc?.lat, userLoc?.lng]);

  const cartItemsCount = cart.reduce((total, item) => total + item.quantity, 0);

  const filteredRestaurants = restaurants.filter(r => {
    // Defensive: restaurantes recién registrados pueden no tener category/name aún
    const name = (r.name ?? '').toLowerCase();
    const category = (r.category ?? '').toLowerCase();
    const q = searchQuery.toLowerCase();
    // `idDeCategoria` entiende tanto el id nuevo como el texto viejo, asi que
    // un restaurante que todavia no se haya migrado sigue saliendo. Antes era
    // `===` pelado y cualquier diferencia lo escondia.
    const matchesCategory = activeCategory
      ? idDeCategoria(r.category) === idDeCategoria(activeCategory)
      : true;
    const matchesSearch = name.includes(q) || category.includes(q);
    return matchesCategory && matchesSearch;
  });

  // DISTRIBUCIÓN POR PLAN DE ANUNCIO (no son membresías: recibir pedidos es
  // gratis para todos).
  //   - ANUNCIO DESTACADO (RD$2,000): sección "Destacados" con tarjeta dorada y
  //     banner en la portada. Orden randomizado entre ellos para exposición
  //     equitativa.
  //   - ANUNCIO BÁSICO (RD$1,000): NO entra aquí. Sube por encima de los que no
  //     pagan dentro de su categoría (el orden del catálogo va por isPremium),
  //     pero la portada y el banner quedan reservados al Destacado — si no, el
  //     que paga el doble no recibiría nada a cambio.
  //   - SIN ANUNCIO: abajo en "Todos los negocios", por rating.
  const esDestacado = (r: any) =>
    r.adPlan === 'DESTACADO' ||
    // Compatibilidad: restaurantes activados antes de que existiera el Básico
    // no tienen `adPlan`; se tratan como Destacado, que es lo que pagaron.
    (r.isPremium === true && !r.adPlan);

  const sponsoredList = React.useMemo(() => {
    const arr = filteredRestaurants.filter(esDestacado);
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  // Re-shuffle solo cuando la lista cambia de tamaño o de categoría
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRestaurants.length, activeCategory]);

  // Enriquece cada restaurante con `distanceKm` si tenemos ubicación del user
  const withDistance = (list: any[]) => {
    if (!userLoc) return list;
    return list.map((r: any) => {
      const lat = r.location?.lat ?? r.lat;
      const lng = r.location?.lng ?? r.lng;
      if (typeof lat !== 'number' || typeof lng !== 'number') {
        return { ...r, distanceKm: null };
      }
      return {
        ...r,
        distanceKm: haversineKm({ lat: userLoc.lat, lng: userLoc.lng }, { lat, lng }),
      };
    });
  };

  // Resto (sin anuncio y con Anuncio Básico): 1° por distancia, 2° por rating.
  // El Básico no sale en "Destacados", pero el catálogo lo trae ordenado por
  // isPremium desde el servidor, así que igual aparece antes que los que no
  // pagan anuncio — que es exactamente lo que compró.
  const regularList = withDistance(
    filteredRestaurants.filter((r: any) => !esDestacado(r))
  ).sort((a: any, b: any) => {
    if (a.distanceKm !== null && b.distanceKm !== null) {
      return a.distanceKm - b.distanceKm;
    }
    if (a.distanceKm !== null) return -1;
    if (b.distanceKm !== null) return 1;
    const rA = parseFloat(a.rating ?? '0');
    const rB = parseFloat(b.rating ?? '0');
    return rB - rA;
  });

  // BANNERS PUBLICITARIOS — exclusivos del Anuncio Destacado (RD$2,000).
  // Cada restaurante puede tener varias promociones activas; cada una se
  // convierte en un banner. Si un destacado NO tiene promos, generamos un
  // banner por defecto para que igual reciba lo que pagó.
  const sponsoredBanners: PromoBannerItem[] = restaurants
    .filter((r: any) => esDestacado(r) && r.status === 'APPROVED')
    .flatMap((r: any) => {
      const promos = Array.isArray(r.activePromotions) ? r.activePromotions : [];
      if (promos.length === 0) {
        return [{
          id: `${r.id}-default`,
          restaurantId: r.id,
          restaurantName: r.name ?? 'Negocio',
          restaurantLogoUrl: r.logoUrl ?? null,
          restaurantCoverUrl: r.coverUrl ?? null,
          title: '¡Descubre nuestro menú!',
          description: r.description ?? 'Pide y recibe en minutos.',
          color: Colors.primary,
          icon: '⭐',
        }];
      }
      return promos.map((p: any, idx: number) => ({
        id: `${r.id}-${p.id ?? idx}`,
        restaurantId: r.id,
        restaurantName: r.name ?? 'Negocio',
        restaurantLogoUrl: r.logoUrl ?? null,
        restaurantCoverUrl: r.coverUrl ?? null,
        title: p.title ?? 'Promoción especial',
        description: p.description ?? '',
        color: p.color ?? Colors.primary,
        icon: p.icon ?? '🔥',
      }));
    });

  return (
    <SafeAreaView style={styles.safeArea}>
      {/* HEADER */}
      <View style={styles.header}>
        <View>
          {/* Decía "Av. Winston Churchill 105" escrito a mano, para todo el
              mundo y sin poder cambiarlo — el cliente creía que su pedido iba
              a una dirección que no es la suya. Ahora muestra la que eligió y
              lleva a la pantalla de direcciones, que ya existía. */}
          <Text style={styles.deliverToText}>Entregar en</Text>
          <TouchableOpacity style={styles.addressSelector} onPress={() => router.push('/addresses')}>
            <Text style={styles.addressText} numberOfLines={1}>
              {direccionEntrega?.line1 || 'Elige tu dirección'}
            </Text>
            <Ionicons name="chevron-down" size={16} color={Colors.primary} />
          </TouchableOpacity>
        </View>
        <View style={styles.headerRight}>
          <TouchableOpacity 
            style={styles.cartBtn}
            onPress={() => router.push('/cart')}
          >
            <Ionicons name="cart-outline" size={24} color={Colors.text} />
            {cartItemsCount > 0 && (
              <View style={styles.badge}>
                <Text style={styles.badgeText}>{cartItemsCount}</Text>
              </View>
            )}
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 100 }}>
        {isOffline && (
          <View style={{
            backgroundColor: '#FFF3CD',
            paddingVertical: 8,
            paddingHorizontal: SIZES.padding,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 8,
          }}>
            <Ionicons name="cloud-offline-outline" size={14} color="#856404" />
            <Text style={{ fontSize: 12, color: '#856404', marginLeft: 6, fontWeight: '600' }}>
              Modo offline — mostrando últimos negocios vistos
            </Text>
          </View>
        )}

        {/* BUSCADOR */}
        <View style={styles.searchContainer}>
          <Ionicons name="search" size={20} color={Colors.textSecondary} style={styles.searchIcon} />
          <TextInput 
            style={styles.searchInput}
            placeholder="Comida, farmacia, supermercado, colmado…"
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')} style={{ padding: 5 }}>
              <Ionicons name="close-circle" size={20} color={Colors.textSecondary} />
            </TouchableOpacity>
          )}
        </View>

        {/* BANNERS PUBLICITARIOS AUTO-ROTATIVOS */}
        {/* Solo restaurantes que pagaron el Plan Destacado aparecen aquí.
            Cambian solos cada 5 segundos. El orden se randomiza al cargar
            para dar exposición equitativa a todos los anunciantes. */}
        {sponsoredBanners.length > 0 && (
          <RotatingPromoBanner items={sponsoredBanners} intervalMs={5000} />
        )}

        {/* CATEGORIAS */}
        <Text style={styles.sectionTitle}>Categorías</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.categoriesContainer}>
          {/* Se navega con el ID (lo que esta guardado), y se PINTA el nombre.
              Antes se mandaba la etiqueta, que es justo lo que se desincronizo. */}
          {CATEGORIAS_DE_NEGOCIO.map((cat) => (
            <TouchableOpacity
              key={cat.id}
              style={[
                styles.categoryCard,
                activeCategory === cat.id && styles.categoryCardActive
              ]}
              onPress={() => router.push({ pathname: '/food-category', params: { category: cat.id, icon: cat.icono } } as any)}
            >
              <Text style={styles.categoryIcon}>{cat.icono}</Text>
              <Text style={[styles.categoryName, activeCategory === cat.id && styles.categoryNameActive]}>{cat.nombre}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        {/* ─────────── SECCIÓN: DESTACADOS (Plan Destacado) ─────────── */}
        {sponsoredList.length > 0 && (
          <View>
            <View style={styles.sectionHeader}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Ionicons name="star" size={16} color="#FF9500" />
                <Text style={[styles.sectionTitle, { marginLeft: 6 }]}>Destacados</Text>
              </View>
              <Text style={styles.sectionCount}>{sponsoredList.length}</Text>
            </View>
            <View style={styles.restaurantsContainer}>
              {sponsoredList.map((restaurant: any) => (
                <TouchableOpacity
                  key={restaurant.id}
                  style={[styles.restaurantCard, styles.restaurantCardPremium]}
                  onPress={() => router.push(`/restaurant/${restaurant.id}` as any)}
                  activeOpacity={0.92}
                >
                  <Image source={{ uri: restaurant.coverUrl }} style={styles.restaurantCover} />
                  <View style={styles.premiumRibbon}>
                    <Ionicons name="star" size={11} color="#FFF" />
                    <Text style={styles.premiumRibbonText}>DESTACADO</Text>
                  </View>
                  <View style={styles.restaurantInfo}>
                    <View style={styles.restaurantHeader}>
                      <Text style={styles.restaurantName} numberOfLines={1}>{restaurant.name}</Text>
                      {(() => { const r = ratingDisplay(restaurant.rating, restaurant.ratingCount); return (
                      <View style={styles.ratingBadge}>
                        <Text style={styles.ratingText}>{r.label}</Text>
                        {!r.isNew && <Ionicons name="star" size={12} color="#FF9500" />}
                      </View>
                    ); })()}
                    </View>
                    <Text style={styles.restaurantDesc}>
                      {nombreDeCategoria(restaurant.category) ?? restaurant.category}{restaurant.deliveryTime ? ` • ${restaurant.deliveryTime}` : ''}
                    </Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 5 }}>
                      <Ionicons name="bicycle" size={13} color={Colors.primary} />
                      <Text style={[styles.deliveryFee, { marginLeft: 4 }]}>
                        {pesos(restaurant.deliveryFee ?? 0)} delivery
                      </Text>
                    </View>
                  </View>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        )}

        {/* ─────────── SECCIÓN: TODOS LOS RESTAURANTES (Plan Gratis) ─────────── */}
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>
            {sponsoredList.length > 0 ? 'Más negocios' : 'Negocios'}
          </Text>
          {/* Mientras carga, un "0" también dice "no hay ninguno" (H72). */}
          <Text style={styles.sectionCount}>
            {filteredRestaurants.length === 0 && !restaurantesRecibidos ? '' : regularList.length}
          </Text>
        </View>
        <View style={styles.restaurantsContainer}>
          {filteredRestaurants.length === 0 && !restaurantesRecibidos ? (
            <View style={{ alignItems: 'center', paddingVertical: 30 }}>
              <ActivityIndicator size="large" color={Colors.primary} />
              <Text style={styles.noResultsText}>
                Cargando negocios…
              </Text>
            </View>
          ) : filteredRestaurants.length === 0 ? (
            <View style={{ alignItems: 'center', paddingVertical: 30 }}>
              {/* "para esta búsqueda" solo cuando de verdad se buscó algo.
                  Sin búsqueda, el listener también entrega `[]` cuando la
                  consulta falla: "ahora mismo" es verdad en los dos casos. */}
              <Ionicons name="storefront-outline" size={48} color={Colors.border} />
              <Text style={styles.noResultsText}>
                {searchQuery.trim()
                  ? 'No encontramos negocios para esta búsqueda.'
                  : 'No hay negocios para mostrar ahora mismo.'}
              </Text>
            </View>
          ) : regularList.length === 0 ? null : (
            regularList.map((restaurant: any) => (
              <TouchableOpacity
                key={restaurant.id}
                style={styles.restaurantCard}
                onPress={() => router.push(`/restaurant/${restaurant.id}` as any)}
                activeOpacity={0.92}
              >
                <Image source={{ uri: restaurant.coverUrl }} style={styles.restaurantCover} />
                <View style={styles.restaurantInfo}>
                  <View style={styles.restaurantHeader}>
                    <Text style={styles.restaurantName} numberOfLines={1}>{restaurant.name}</Text>
                    {(() => { const r = ratingDisplay(restaurant.rating, restaurant.ratingCount); return (
                      <View style={styles.ratingBadge}>
                        <Text style={styles.ratingText}>{r.label}</Text>
                        {!r.isNew && <Ionicons name="star" size={12} color="#FF9500" />}
                      </View>
                    ); })()}
                  </View>
                  <Text style={styles.restaurantDesc}>
                    {nombreDeCategoria(restaurant.category) ?? restaurant.category}{restaurant.deliveryTime ? ` • ${restaurant.deliveryTime}` : ''}
                  </Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 5, flexWrap: 'wrap' }}>
                    <Ionicons name="bicycle" size={13} color={Colors.primary} />
                    <Text style={[styles.deliveryFee, { marginLeft: 4 }]}>
                      {pesos(restaurant.deliveryFee ?? 0)} delivery
                    </Text>
                    {typeof restaurant.distanceKm === 'number' && (
                      <View style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 10 }}>
                        <Ionicons name="navigate" size={12} color={Colors.textSecondary} />
                        <Text style={{ fontSize: 12, color: Colors.textSecondary, marginLeft: 3 }}>
                          {restaurant.distanceKm < 1
                            ? `${Math.round(restaurant.distanceKm * 1000)} m`
                            : `${restaurant.distanceKm.toFixed(1)} km`}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
              </TouchableOpacity>
            ))
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#FFF' },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: SIZES.padding, paddingTop: 10, paddingBottom: 15 },
  deliverToText: { fontSize: 12, color: Colors.textSecondary },
  addressSelector: { flexDirection: 'row', alignItems: 'center', marginTop: 2 },
  addressText: { fontSize: 14, fontWeight: 'bold', color: Colors.primary, marginRight: 4 },
  headerRight: { flexDirection: 'row', alignItems: 'center' },
  cartBtn: { position: 'relative', padding: 5 },
  badge: { position: 'absolute', top: -2, right: -5, backgroundColor: Colors.accent, borderRadius: 10, width: 18, height: 18, justifyContent: 'center', alignItems: 'center' },
  badgeText: { color: '#FFF', fontSize: 10, fontWeight: 'bold' },
  
  searchContainer: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F5F5F5', marginHorizontal: SIZES.padding, borderRadius: 12, paddingHorizontal: 15, height: 45, marginBottom: 20 },
  searchIcon: { marginRight: 10 },
  searchInput: { flex: 1, fontSize: 14, color: Colors.text, outlineStyle: 'none' as any },

  promoCarousel: { marginBottom: 20 },
  promoCard: { width: 280, height: 100, borderRadius: 16, padding: 15, flexDirection: 'row', alignItems: 'center', marginRight: 15 },

  sectionTitle: { fontSize: 18, fontWeight: 'bold', color: Colors.text },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: SIZES.padding,
    marginBottom: 14,
    marginTop: 4,
  },
  sectionCount: {
    fontSize: 12,
    color: Colors.textSecondary,
    backgroundColor: '#F2F2F2',
    paddingHorizontal: 9,
    paddingVertical: 3,
    borderRadius: 10,
    overflow: 'hidden',
    fontWeight: '700',
  },
  
  categoriesContainer: { paddingHorizontal: SIZES.padding, paddingBottom: 20, gap: 15 },
  categoryCard: { alignItems: 'center', width: 70 },
  categoryCardActive: { transform: [{ scale: 1.05 }] },
  categoryIcon: { fontSize: 32, backgroundColor: '#F5F5F5', padding: 15, borderRadius: 20, overflow: 'hidden' },
  categoryName: { fontSize: 12, color: Colors.textSecondary, marginTop: 5, fontWeight: '500' },
  categoryNameActive: { color: Colors.primary, fontWeight: 'bold' },

  restaurantsContainer: { paddingHorizontal: SIZES.padding, paddingBottom: 20 },
  noResultsText: { textAlign: 'center', color: Colors.textSecondary, marginTop: 10 },
  restaurantCard: {
    backgroundColor: '#FFF',
    borderRadius: 16,
    marginBottom: 18,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.08,
    shadowRadius: 6,
    elevation: 3,
    overflow: 'hidden',
    position: 'relative',
  },
  // Card del Plan Destacado — borde dorado, sombra ámbar, ribbon arriba-izquierda
  restaurantCardPremium: {
    borderWidth: 2,
    borderColor: '#FFC107',
    shadowColor: '#FF9500',
    shadowOpacity: 0.18,
    shadowRadius: 8,
    elevation: 5,
  },
  premiumRibbon: {
    position: 'absolute',
    top: 12,
    left: 12,
    zIndex: 2,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FF9500',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 4,
    elevation: 4,
  },
  premiumRibbonText: {
    color: '#FFF',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.5,
    marginLeft: 4,
  },
  restaurantCover: { width: '100%', height: 150 },
  restaurantInfo: { padding: 15 },
  restaurantHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 },
  restaurantName: { fontSize: 16, fontWeight: 'bold', color: Colors.text },
  ratingBadge: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#FFF8E1', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 10 },
  ratingText: { fontSize: 12, fontWeight: 'bold', color: '#FF9500', marginRight: 2 },
  restaurantDesc: { fontSize: 13, color: Colors.textSecondary, marginBottom: 5 },
  deliveryFee: { fontSize: 12, color: Colors.primary, fontWeight: '500' },
});
